// MorningBlend.jsx — the "Blend" branch of Morning Mode (Agent H).
//
// A 3-step guided mix for the morning the canister remnant has dropped below one
// brew's dose and a fresh bag is on hand: finish the remnant by topping it up
// with the new bag, in a ratio the AI biases toward the NEW bag (it dominates the
// cup). This is a ONE-OFF: it decrements BOTH bags by their share and writes NO
// calibration history to either bean — so, deliberately, there is no rating push
// afterward (nothing to calibrate). See store.logBlendBrew.
//
// The seam (from MorningCoffee): this component owns the whole Blend body. The AI
// endpoint (/api/brew-recipe, source:"blend") echoes the client-provided split
// UNCHANGED and refines grind/temp/ratio around the NEW bag; if it can't be
// reached we fall back to a deterministic compromise so the flow never dead-ends.

import { useMemo, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { GlassCard, PrimaryButton, SecondaryButton, Segmented, SpecTiles, Eyebrow, Chip, ListRow } from "./pronto";
import { AidenProfileBanner } from "./AidenProfileBanner";
import { RecipeNotes } from "./RecipeNotes";
import { generateBrewRecipe, recipeToBrew } from "../lib/morning-ai";
import { getOpenBeans, findActiveBags } from "../lib/brew-helpers";

const MODES = [{ id: "hot", label: "Hot" }, { id: "cold", label: "Cold brew" }];
const cToF = (c) => (c == null ? null : Math.round((c * 9) / 5 + 32));
const round = (n) => Math.round(+n || 0);
const gramsLeft = (b) => +(b?.weight_left_g ?? b?.weight_g ?? 0);

// Bare inside the MorningCoffee one-card frame; own card standalone.
function Card({ embedded, children }) {
  return embedded ? <div>{children}</div> : <GlassCard className="p-5">{children}</GlassCard>;
}

export function MorningBlend({ store, session, resolved, onComplete, embedded = false }) {
  const { getToken } = useAuth();

  const remnant = resolved?.remnant || null;
  const cups = resolved?.cups || 8;
  // Total dose we aim to hit for the cup (a full brew's worth). The remnant is
  // low by definition on this branch; the new bag tops it up to a full dose.
  const totalDose = round(resolved?.doseG) || 65;

  // Candidate new bags: any open/active bag that isn't the remnant. Default to the
  // one branch resolution already picked.
  const newBagOptions = useMemo(() => {
    const open = getOpenBeans(store.beans, store.preferences);
    const pool = open.length ? open : findActiveBags(store.beans);
    return pool.filter((b) => b.id !== remnant?.id);
  }, [store.beans, store.preferences, remnant?.id]);

  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("hot");
  // Step 1 — grams of the remnant to use. Default = everything left in the bag.
  const remnantMax = Math.max(0, round(gramsLeft(remnant)));
  const [remnantGrams, setRemnantGrams] = useState(remnantMax || 1);
  // Step 2 — the new bag to blend in.
  const [newBagId, setNewBagId] = useState(resolved?.newBag?.id || newBagOptions[0]?.id || null);
  const newBag = useMemo(
    () => newBagOptions.find((b) => b.id === newBagId) || resolved?.newBag || newBagOptions[0] || null,
    [newBagOptions, newBagId, resolved?.newBag],
  );

  // Step 3 — the generated ratio recipe + the two-bag split.
  const [recipe, setRecipe] = useState(null);
  const [components, setComponents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [busy, setBusy] = useState(false);

  // New-bag grams = whatever it takes to reach the total dose. Always >= 1 so both
  // sides of the blend are positive (guardrail: never a single dose off one bag).
  const newGrams = Math.max(1, totalDose - Math.min(remnantGrams, totalDose - 1));

  if (!remnant) {
    return (
      <GlassCard className="p-5 text-center">
        <div className="text-[13px] text-pronto-text-2">No remnant bag to blend. Pick a bag in Coffee to start.</div>
      </GlassCard>
    );
  }
  if (!newBag) {
    return (
      <GlassCard className="p-5 text-center">
        <div className="text-[13px] text-pronto-text-2">
          Only {remnant.name} is open — add a second bag to blend into.
        </div>
      </GlassCard>
    );
  }

  const bump = (delta) =>
    setRemnantGrams((g) => Math.max(1, Math.min(remnantMax || totalDose, round(g) + delta)));

  // Deterministic compromise recipe — used when the AI endpoint can't be reached.
  // Grind = the average of each bag's own ideal grind; temp/ratio/bloom follow the
  // NEW bag (it dominates the cup, per the blend bias).
  const buildFallback = (split) => {
    const rBrew = store.computeBrew({ beanId: remnant.id, cups, mode });
    const nBrew = store.computeBrew({ beanId: newBag.id, cups, mode });
    const grinds = [rBrew?.grind?.setting, nBrew?.grind?.setting].filter((n) => Number.isFinite(+n));
    const compromiseGrind = grinds.length
      ? Math.round(grinds.reduce((s, n) => s + +n, 0) / grinds.length)
      : nBrew?.grind?.setting;
    const ratio = nBrew?.aidenProfile?.ratio ?? nBrew?.ratio ?? 15;
    return {
      dose_g: totalDose,
      cups,
      grind_setting: compromiseGrind,
      aiden_profile: nBrew?.aidenProfile,
      params: {
        temp_f: cToF(nBrew?.temp_c),
        temp_c: nBrew?.temp_c,
        ratio,
        bloom_s: nBrew?.aidenProfile?.bloomDuration ?? nBrew?.bloom_time_s,
        bloom_temp_f: nBrew?.aidenProfile?.bloomEnabled ? cToF(nBrew?.aidenProfile?.bloomTemperature) : null,
      },
      rationale:
        `Couldn't reach the blend AI — using a compromise grind (avg of each bag's ideal) ` +
        `biased to ${newBag.name}. One-off blend.`,
      source: "blend",
      blend_components: split,
      refined: false,
    };
  };

  // Step 3 trigger: build the split client-side (the endpoint won't invent bag
  // weights), ask the AI to refine a ratio recipe around the NEW bag, and render.
  const generate = async () => {
    if (loading) return;
    setLoading(true);
    setUsedFallback(false);
    // blend_components: [{bean_id, grams}, {bean_id, grams}] — both positive.
    const split = [
      { bean_id: remnant.id, grams: Math.min(remnantGrams, remnantMax || remnantGrams) },
      { bean_id: newBag.id, grams: newGrams },
    ];
    setComponents(split);
    try {
      const card = await generateBrewRecipe({
        getToken,
        bean: newBag, // bias the recipe toward the new bag's ideal
        tasteProfile: store.tasteProfile,
        mode,
        cups,
        remnant: { bean_id: remnant.id, name: remnant.name, grams: split[0].grams },
        newBag: { bean_id: newBag.id, name: newBag.name },
        blend_components: split,
        grinder: store.gear?.grinder,
        source: "blend",
      });
      // Endpoint echoes our split; if it didn't, keep ours. Display dose is the
      // total of the two bags regardless of what the single-bag math returned.
      const merged = {
        ...card,
        dose_g: totalDose,
        blend_components: Array.isArray(card?.blend_components) ? card.blend_components : split,
      };
      setRecipe(merged);
      setComponents(merged.blend_components);
    } catch (e) {
      // Transport failure only (the endpoint itself degrades gracefully). Never a
      // dead end — fall back to the deterministic compromise.
      console.warn("blend recipe generate failed", e?.message);
      setRecipe(buildFallback(split));
      setComponents(split);
      setUsedFallback(true);
    } finally {
      setLoading(false);
      setStep(3);
    }
  };

  // "Started brew" — the one blend transaction. Decrement BOTH bags by their share
  // via store.logBlendBrew ONLY (writes bean_id null → no calibration history),
  // then complete Morning Mode. Intentionally NO rating push: a blend is one-off
  // and calibration-free, so there is nothing to rate later.
  const startBrew = async () => {
    if (busy || !recipe) return;
    setBusy(true);
    try {
      const ratio = recipe?.params?.ratio || recipe?.aiden_profile?.ratio || 15;
      const brew = {
        ...recipeToBrew(recipe, newBag),
        // The blend cup is the sum of both bags — pin dose/water to the total so the
        // logged brew matches the split, not the single-bag math.
        dose_g: totalDose,
        water_g: Math.round(totalDose * ratio),
        source: "blend",
        rationale: recipe.rationale, // carry the blend rationale
      };
      const log = await store.logBlendBrew(brew, components);
      await store.completeMorning(log.id);
      onComplete?.(log);
    } finally {
      setBusy(false);
    }
  };

  const p = recipe?.params || {};
  const tempF = p.temp_f ?? cToF(p.temp_c);

  const bannerBrew = recipe ? recipeToBrew(recipe, newBag) : null;

  return (
    <Card embedded={embedded}>
      {!embedded && <Eyebrow className="mb-2">Blend · one-off</Eyebrow>}
      <Segmented variant="pill" options={MODES} value={mode} onChange={setMode} className="mb-4" />

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-4">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className={`h-1 flex-1 rounded-full ${n <= step ? "bg-pronto-navy" : "bg-[var(--pg-6)]"}`}
          />
        ))}
      </div>

      {/* ---- Step 1 — beans left (remnant) ---- */}
      {step === 1 && (
        <>
          <div className="text-[13px] text-pronto-text-2 mb-1">Step 1 of 3 · Beans left</div>
          <div className="font-round font-bold text-[17px] text-pronto-ink leading-tight">{remnant.name}</div>
          <div className="text-[12.5px] text-pronto-text-2 mb-4">
            {[remnant.roaster, remnant.roast_level].filter(Boolean).join(" · ")}
            {remnantMax ? ` · ~${remnantMax}g left` : ""}
          </div>

          <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mb-1">Use from this bag</div>
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => bump(-1)}
              className="w-9 h-9 rounded-full bg-[var(--pg-7)] border border-[var(--p-sep-12)] text-pronto-ink font-round text-[20px] leading-none grid place-items-center disabled:opacity-40"
              disabled={remnantGrams <= 1}
              aria-label="Less"
            >
              −
            </button>
            <div className="flex-1 text-center">
              <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">{remnantGrams}g</div>
              <div className="text-[11px] text-pronto-text-3 mt-1">of {remnantMax || "?"}g remaining</div>
            </div>
            <button
              onClick={() => bump(1)}
              className="w-9 h-9 rounded-full bg-[var(--pg-7)] border border-[var(--p-sep-12)] text-pronto-ink font-round text-[20px] leading-none grid place-items-center disabled:opacity-40"
              disabled={!!remnantMax && remnantGrams >= remnantMax}
              aria-label="More"
            >
              +
            </button>
          </div>
          {remnantMax > 0 && (
            <button
              onClick={() => setRemnantGrams(remnantMax)}
              className="text-[12px] text-pronto-navy font-medium mb-4"
            >
              Use all {remnantMax}g
            </button>
          )}

          <button onClick={() => setStep(2)}
          className="w-full mt-1 h-11 rounded-[13px] bg-[rgba(63,74,99,0.08)] text-pronto-navy font-sf text-[15px] font-semibold active:bg-[rgba(63,74,99,0.15)]">Add bag</button>
        </>
      )}

      {/* ---- Step 2 — the new bag ---- */}
      {step === 2 && (
        <>
          <div className="text-[13px] text-pronto-text-2 mb-2">Step 2 of 3 · Blend into</div>
          <div className="flex flex-col gap-1.5 mb-4">
            {newBagOptions.map((b) => (
              <button
                key={b.id}
                onClick={() => setNewBagId(b.id)}
                className={`text-left rounded-[13px] px-3 py-2.5 border transition-colors ${
                  b.id === newBag.id
                    ? "bg-pronto-solid text-white border-transparent"
                    : "bg-[var(--pg-7)] border-[var(--p-sep-12)] text-pronto-ink"
                }`}
              >
                <div className="font-sf font-semibold text-[14px] leading-tight">{b.name}</div>
                <div className={`text-[12px] ${b.id === newBag.id ? "text-white/70" : "text-pronto-text-2"}`}>
                  {[b.roaster, b.roast_level].filter(Boolean).join(" · ")}
                  {gramsLeft(b) ? ` · ~${round(gramsLeft(b))}g` : ""}
                </div>
              </button>
            ))}
          </div>

          <div className="flex gap-2 mt-1">
            <SecondaryButton onClick={() => setStep(1)} className="flex-1">Back</SecondaryButton>
            <PrimaryButton onClick={generate} disabled={loading || !newBag} className="flex-1">
              {loading ? "Mixing…" : "Get ratio"}
            </PrimaryButton>
          </div>
        </>
      )}

      {/* ---- Step 3 — AI ratio recipe ---- */}
      {step === 3 && recipe && (
        <>
          <div className="text-[13px] text-pronto-text-2 mb-2">Step 3 of 3 · The blend</div>

          {/* Ratio label — the two-bag split */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            <Chip tone="accent">{components?.[0]?.grams ?? remnantGrams}g remnant</Chip>
            <span className="text-pronto-text-3">+</span>
            <Chip tone="selected">{components?.[1]?.grams ?? newGrams}g new</Chip>
          </div>
          <div className="text-[12.5px] text-pronto-text-2 mb-4 leading-snug">
            <span className="text-pronto-ink font-medium">{remnant.name}</span> topped up with{" "}
            <span className="text-pronto-ink font-medium">{newBag.name}</span> — the new bag leads the cup.
          </div>

          {/* Dose + grind (total across both bags) */}
          <div className="flex items-end gap-5 mb-4">
            <div>
              <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">
                {round(recipe.dose_g)}g
              </div>
              <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mt-1">total · {cups} cups</div>
            </div>
            <div>
              <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">
                {recipe.grind_setting ?? "—"}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mt-1">Ode grind</div>
            </div>
          </div>

          <SpecTiles
            className="mb-3"
            items={[
              { label: "Temp", value: tempF ? `${tempF}°` : "—" },
              { label: "Ratio", value: p.ratio ? `1:${p.ratio}` : "—" },
              { label: "Bloom", value: p.bloom_s ? `${p.bloom_s}s` : "—" },
            ]}
          />

          {recipe.rationale && (
            <div className="text-[12px] text-pronto-text-2 mb-2 leading-snug">{recipe.rationale}</div>
          )}
          {usedFallback && (
            <div className="text-[11.5px] text-pronto-text-3 mb-2">Offline compromise — the AI ratio wasn't reachable.</div>
          )}

          {/* One-off note — this never changes either bag's saved recipe. */}
          <div className="rounded-[11px] bg-[var(--pg-6)] px-3 py-2 text-[11.5px] text-pronto-text-2 mb-4 leading-snug">
            One-off blend — it decrements both bags by their share but won't change either bag's saved recipe or your
            taste calibration.
          </div>

          {/* Aiden profile banner — a blend saves a brand-new one-off profile */}
          <RecipeNotes beans={[remnant, newBag]} brew={bannerBrew} grinder={store.gear?.grinder} className="mb-3" />

          <AidenProfileBanner store={store} bean={newBag} brew={bannerBrew} branch="blend" className="mb-3" />

          <div className="flex gap-2">
            <SecondaryButton onClick={() => setStep(2)} className="flex-1" disabled={busy}>Back</SecondaryButton>
            <button
              onClick={startBrew}
              disabled={busy}
              className="flex-1 h-[46px] rounded-[14px] font-sf text-[15px] font-semibold text-pronto-navy disabled:opacity-60"
              style={{ background: "rgba(63,74,99,0.1)" }}
            >
              {busy ? "Logging…" : "Started the Aiden ✓"}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}
